import { useEffect, useRef } from 'react';
import { pullSystemActivity, useSystemActivity } from '@/features/desktop/hooks/useSystemActivity';
import { useViewer } from '@/hooks/useShellData';
import { useActivityMonitor } from './hooks/useActivityMonitor';
import { presenceApi } from './presenceClient';
import { presenceActions } from './presenceStore';
import { watchPresence } from './presenceWatcher';
import { measurePresenceHere, resetSystemActivity } from './systemActivity';

/**
 * Headless. Mount once inside the app providers.
 *
 * It renders nothing, so the automatic-presence machinery (idle detection,
 * override/status expiry, heartbeat) never re-renders a single consumer —
 * subscribers read the store directly. The viewer query is the same cached
 * entry the shell already reads, so binding the self id costs no extra request.
 *
 * It is also the ONLY place the signed-in user's own declaration is loaded.
 * Without that read the store started from its seeded placeholder on every
 * navigation, so a user who had set Do not disturb and a status message saw
 * their own chip come back as plain "Available" after any reload — while
 * everyone else, reading the same state over the API, still saw the truth.
 */
export function PresenceRuntime() {
  /**
   * THIS DOCUMENT IS THE ONE WATCHING - DECLARED FIRST. Effects run in the order
   * they are written, and `useActivityMonitor`'s first beat recomputes the
   * chip at mount. Declared below it, that beat ran before the page had been
   * granted "somebody opened this", found no evidence of anybody, and painted
   * the user AWAY on every hard refresh and every sign-in - until the next beat
   * fifteen seconds later. See `measurePresenceHere`, which now also recomputes
   * the moment it grants, so the order can never matter again.
   *
   * Declared rather than assumed, because a desktop call window mounts
   * `RealtimeRuntime` without mounting this - and a document with no idle
   * monitor, no OS reports and no tick would otherwise report its user Away
   * five minutes after the call window opened, while they were talking into it.
   */
  useEffect(() => measurePresenceHere(), []);

  useActivityMonitor();
  /* THE SAME QUESTION, ASKED OF THE MACHINE. `useActivityMonitor` can only see
     input delivered to this document; on the desktop the shell can see the last
     input to ANY application, which is what Teams measures and what a user
     working in another window has been doing all along. A no-op in a browser,
     which has no such API - see features/presence/systemActivity.ts. */
  useSystemActivity();

  const viewerId = useViewer().data?.id;

  /**
   * ONE PERSON'S IDLE EVIDENCE IS NOT ANOTHER'S.
   *
   * `lastInputAt`, `lastSystemAt`, the lock flag and the window-visibility flag
   * are module state, so they outlive any component and describe whoever this
   * document belonged to when they were written. Nothing cleared them. Sign out
   * and back in as somebody else, or let the desktop shell swap the session
   * under the same document, and the new user inherited the old one's readings
   * - including a lock, which would have held them at Away with nothing to
   * clear it. `resetSystemActivity` and `presenceActions.forgetIdleEvidence`
   * were written for exactly this and had no caller in the product at all;
   * their docblocks described a gesture nobody made.
   *
   * THE VIEWER CHANGING IS THE WHOLE SIGNAL, and unmounting is not part of it.
   * `AppOverlays` mounts this from `AppProviders`, which is above the router and
   * never comes down for a sign-out - so an unmount cleanup would fire only when
   * the document is being destroyed, where there is nothing left to protect, and
   * would fire SPURIOUSLY on React's StrictMode double-mount in development.
   * Signing out clears the viewer query, so `viewerId` goes undefined and this
   * effect catches it; the ref makes the StrictMode re-run a no-op, because the
   * viewer did not change.
   */
  const previousViewerId = useRef<string | null>(null);
  useEffect(() => {
    const previous = previousViewerId.current;
    const current = viewerId ?? null;
    previousViewerId.current = current;
    if (previous === null || previous === current) return undefined;
    resetSystemActivity();
    /* THE PERSON CHANGED; THE MACHINE DID NOT. The reset drops the lock and the
       OS reading with the person's evidence, and a locked or idle machine
       pushes nothing to correct that - so ask the shell, exactly as a fresh
       document does on mount. Without it the next person read Available on a
       locked workstation until the document-load seed aged out. */
    return pullSystemActivity();
  }, [viewerId]);

  useEffect(() => {
    if (!viewerId) return;
    presenceActions.setSelfId(viewerId);

    let cancelled = false;
    void presenceApi
      .fetchSelf()
      .then((declaration) => {
        if (!cancelled) presenceActions.hydrateSelf(declaration);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [viewerId]);

  /*
   * WATCH YOURSELF, LIKE ANY OTHER PERSON.
   *
   * `watchPresence` is called by the components that RENDER somebody — an
   * avatar, a roster row — and nothing renders you that way, so your own id was
   * never in the subscription set. Your snapshot therefore only ever had the
   * one-time `/presence/me` fetch above plus whatever this client guessed
   * locally, and no server update could reach it.
   *
   * That is the reported asymmetry exactly: colleagues saw you go Away because
   * they were subscribed to you, while your own chip sat on whatever it last
   * decided for itself. Subscribing here makes `applyDeltas` — which already
   * merges a row for `selfId` into `state.self` — actually receive one.
   */
  useEffect(() => {
    if (!viewerId) return undefined;
    return watchPresence([viewerId]);
  }, [viewerId]);

  return null;
}
